import { z } from 'zod';
/**
 * AgentTodo — a row in the CEO's agent-action inbox. Distinct from the
 * existing `todo.ts` (which serves L10 wiring). An AgentTodo is always
 * "something the agent (or a person) wants the CEO to look at"; some
 * rows are *blocking* — the agent has drafted an action but won't
 * execute until the CEO approves.
 *
 * Three-state lifecycle:
 *   - `open`     — visible in the inbox, no action required yet
 *   - `blocking` — agent is paused on CEO approval (renders with the
 *                  red `is-blocking` modifier in `<TodoRow>`)
 *   - `done`     — completed; surfaced in the "completed today" tail
 *
 * Source discriminator distinguishes auto-generated agent suggestions
 * from human-assigned tasks and platform-emitted system todos. Each
 * source carries the minimum context the row needs to render its
 * "from X" pill.
 *
 * PR2 ships the schema standalone so `<TodoRow>` can type its `item`
 * prop. The CRUD endpoints land with epic #17 (Todo inbox).
 */
export const AGENT_TODOS_COLLECTION = 'agentTodos';
/**
 * Naming note: `todo.ts` already owns `TodoPriority` / `TodoStatus` for
 * the L10 wiring (with `low|normal|high` × `todo|in_progress|done`
 * lifecycle). The agent-action inbox needs a different shape
 * (`high|med|low` × `open|blocking|done`), so the schemas here are
 * prefixed `AgentTodo*` to coexist in the package barrel. See plan §5.5
 * (Todo type collision) for the decision.
 */
export const AgentTodoPriority = z.enum(['high', 'med', 'low']);
export const AgentTodoStatus = z.enum(['open', 'blocking', 'done']);
export const AgentTodoSource = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('agent'),
        /** Originating insight id, when the todo was synthesized from a signal. */
        insightId: z.string().optional(),
    }),
    z.object({
        kind: z.literal('person'),
        uid: z.string().min(1),
    }),
    z.object({
        kind: z.literal('system'),
    }),
]);
export const AgentTodo = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    ownerUid: z.string().min(1),
    title: z.string().min(1).max(280),
    source: AgentTodoSource,
    /** ISO date (YYYY-MM-DD or full ISO timestamp). */
    due: z.string().optional(),
    priority: AgentTodoPriority,
    status: AgentTodoStatus,
    /** Optional href into the source surface (insight id, dossier slug, etc.). */
    srcRef: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
